import { useEffect, useMemo, useState } from "react";
import "../../../css/CheckDetails/CheckDetails.css";

import StatusButton from "../../Components/Buttons/StatusButton";
import { ReactComponent as ArrowUp } from "../../../assets/icons/arrowUp.svg";

import { colorForResult } from "../Utils/getColorForDecision";
import { computeOverallOutcome } from "../Utils/outcome/checkOutcome";
import { getAllMatchesResolved } from "../Utils/Experian/allMatchesHaveReasons";

// use the LIVE subscription (same as PepSanctions)
import { subscribePepSanReasonsByRef } from "../Utils/Experian/pepSanReasonsStore";

const norm = (v) => (v ?? "").toString().trim().toLowerCase();

const CheckOutcome = ({
  check,
  experianResponse,
  checkType,
  ownerEmail,
  checkId,
}) => {
  const [open, setOpen] = useState(false);
  const [reasonsByRef, setReasonsByRef] = useState({});
  const [reasonsErr, setReasonsErr] = useState("");

  // unwrap Experian wrapper shape (Firestore doc)
  const effectiveExperian = useMemo(
    () => experianResponse?.response ?? experianResponse ?? null,
    [experianResponse]
  );

  // Detect whether PEP has matches (cheap check, no DB call)
  const pepHasMatches = useMemo(() => {
    const r = effectiveExperian;
    const els = r?.clientResponsePayload?.decisionElements || [];
    const pepEl = els.find((d) =>
      ["uk-pepsanc", "watchlist", "watchlistscreening"].includes(
        norm(d?.serviceName)
      )
    );
    const results =
      pepEl?.otherData?.response?.searchResults?.[0]?.results || [];
    return Array.isArray(results) && results.length > 0;
  }, [effectiveExperian]);

  // LIVE subscribe to reasons so changes in PepSanctions rebuild this component
  useEffect(() => {
    let unsub = null;
    let cancelled = false;

    setReasonsErr("");

    // If no matches, no need to subscribe
    if (!pepHasMatches) {
      setReasonsByRef({});
      return;
    }

    // Need identifiers to subscribe
    if (!ownerEmail || !checkId) {
      setReasonsByRef({});
      setReasonsErr("Missing ownerEmail/checkId for PEP reasons lookup.");
      return;
    }

    (async () => {
      try {
        unsub = await subscribePepSanReasonsByRef(
          ownerEmail,
          checkId,
          (next) => {
            if (cancelled) return;
            setReasonsByRef(next || {}); // <- triggers recompute of outcome
          }
        );
      } catch {
        if (!cancelled) {
          setReasonsByRef({});
          setReasonsErr(
            "Could not subscribe to PEP & Sanctions confirmations. Outcome may be incomplete."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      if (typeof unsub === "function") unsub();
    };
  }, [pepHasMatches, ownerEmail, checkId]);

  // This will now recompute every time Firestore snapshot updates
  const outcome = useMemo(() => {
    return computeOverallOutcome({
      rowOrCheck: check,
      checkType,
      experianResponse: effectiveExperian,
      reasonsByRef: reasonsByRef || {},
      getAllMatchesResolved,
    });
  }, [check, checkType, effectiveExperian, reasonsByRef]);

  const statusLower = useMemo(() => norm(outcome?.checkStatus), [outcome]);
  const resultLower = useMemo(() => norm(outcome?.checkResult), [outcome]);

  return (
    <div className="Result-Section">
      <div
        className="Result-Section-Header"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && setOpen((prev) => !prev)
        }
        aria-expanded={open}
      >
        <div className="AML-Text-Subtitle">Check Result</div>

        <div className="Result-Section-Row" style={{ gap: 10 }}>
          {outcome?.checkResult ? (
            <StatusButton
              type={outcome.checkResult}
              color={colorForResult(resultLower)}
            />
          ) : (
            <StatusButton
              type={outcome?.checkStatus || "Processing"}
              color={colorForResult(statusLower)}
            />
          )}

          <ArrowUp
            className={`collapse-icon ${open ? "collapsed" : ""}`}
            style={{ visibility: "hidden" }}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Optional: show reasonsErr somewhere if you want */}
    </div>
  );
};

export default CheckOutcome;
